import { toast } from "sonner";
import { CreativeReferenceOptions } from "./CreativeReferenceOptions";
import { CreativeUploadFrame } from "./CreativeUploadFrame";
import { CreativeUploadPreviews } from "./CreativeUploadPreviews";
import { CreativeUploadKind } from "./CreativeUploadKind";
import { SubjectReferenceTips } from "./SubjectReferenceTips";
import { CharacterChoice } from "./CharacterChoice";
import { CreativeModelFields } from "./CreativeModelFields";
import { attachSubjectReferences } from "./attachSubjectReferences";
import { useBrandSubjects } from "./useBrandSubjects";
import { useModelDraft } from "./useModelDraft";
import { CreativeFilingFields } from "@/components/creative-materials/CreativeFilingFields";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Loader2, Upload } from "@digital-twins/design-system/icons";
import type {
  CreativeSubject,
  CreativeMaterialRole,
  CreativeUsage,
} from "@digital-twins/shared-types";
import { CreativeEditorSection } from "./CreativeEditorDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/Text";
import { FileDropzone } from "@/components/ui/file-dropzone";
import { MaterialHelpDialog } from "@/components/creative-materials/MaterialHelpDialog";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import type { CreativeUploadProps } from "@/types/creative-library";

export function CreativeUpload({
  open,
  embedded,
  brands,
  products,
  onClose,
  onSaved,
  presetRole,
  presetBrandToken,
  presetProductToken,
  onUploaded,
  galleryUpload,
  maxFiles,
  onOpenSubject,
}: CreativeUploadProps) {
  const { t } = useTranslation("app");
  const [files, setFiles] = useState<File[]>([]);
  const file = files[0] ?? null;
  const [fileTitles, setFileTitles] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [role, setRole] = useState<CreativeMaterialRole>("product");
  const [usage, setUsage] = useState<CreativeUsage>("unspecified");
  const [brandToken, setBrandToken] = useState("none");
  const [productToken, setProductToken] = useState("none");
  const [subjectToken, setSubjectToken] = useState("none");
  const [characterKind, setCharacterKind] = useState<"person" | "mascot">(
    "person",
  );
  const [createdToken, setCreatedToken] = useState("");
  const [pending, setPending] = useState<string[]>([]);
  const [attached, setAttached] = useState<CreativeSubject | null>(null);
  const brand = brands.find((item) => item.token === brandToken);
  const needsCharacter = role === "identity" && !galleryUpload;
  const subjectList = useBrandSubjects(
    open && needsCharacter ? brand?.token : undefined,
  );
  const draft = useModelDraft(brandToken, subjectList);
  const { clear: clearDraft } = draft;
  const canEditBrand = Boolean(brand?.canEdit);
  const hasNoModels = subjectList.ready && subjectList.subjects.length === 0;
  const isNewModel =
    needsCharacter &&
    canEditBrand &&
    subjectList.ready &&
    (hasNoModels || draft.chosen);
  const hasModelChoice = isNewModel
    ? Boolean(draft.name.trim())
    : subjectToken !== "none";
  const canSave =
    (files.length > 0 || pending.length > 0) &&
    (!needsCharacter ||
      (brandToken !== "none" && canEditBrand && hasModelChoice));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isSubmitting = useRef(false);
  useEffect(() => {
    if (open) {
      setFiles([]);
      setFileTitles([]);
      setSubjectToken("none");
      clearDraft();
      setCreatedToken("");
      setCharacterKind("person");
      setPending([]);
      setAttached(null);
      setTitle("");
      setRole(presetRole ?? "product");
      setUsage("unspecified");
      setBrandToken(presetBrandToken ?? "none");
      setProductToken(presetProductToken ?? "none");
      setError("");
    }
  }, [open, presetRole, presetBrandToken, presetProductToken, clearDraft]);
  async function save() {
    if (!canSave || isSubmitting.current) {
      return;
    }
    isSubmitting.current = true;
    setSaving(true);
    setError("");
    let completed = 0;
    const uploadedTokens = [...pending];
    let target = subjectToken;
    try {
      if (isNewModel) {
        if (files.length > 20) {
          setError(t("creativeLibrary.characterGalleryCapacity"));
          return;
        }
        try {
          const created = await draft.create();
          target = created.token;
          setSubjectToken(created.token);
          setCharacterKind(created.kind);
          setCreatedToken(created.token);
          onSaved();
        } catch {
          setError(t("creativeLibrary.modelCreateFailed"));
          return;
        }
      } else if (needsCharacter) {
        try {
          const current = await creativeLibraryApi.subject(subjectToken);
          if (
            current.unavailableReferenceCount ||
            new Set([...current.referenceTokens, ...pending]).size +
              files.length >
              20
          ) {
            setError(t("creativeLibrary.characterGalleryCapacity"));
            return;
          }
        } catch {
          setError(t("creativeLibrary.characterAssignmentFailed"));
          return;
        }
      }
      for (let index = 0; index < files.length; index += 1) {
        const next = files[index];
        const body = new FormData();
        body.append("file", next);
        body.append(
          "title",
          files.length === 1
            ? title.trim() || next.name
            : fileTitles[index]?.trim() || next.name,
        );
        body.append("role", role);
        body.append("usage", usage);
        if (brandToken !== "none") body.append("brandToken", brandToken);
        if (role !== "identity" && productToken !== "none")
          body.append("productToken", productToken);
        try {
          const uploaded = await creativeLibraryApi.upload(body);
          if (needsCharacter) {
            uploadedTokens.push(uploaded.token);
            setPending([...uploadedTokens]);
          }
          onUploaded?.(uploaded);
          completed += 1;
        } catch {
          const remaining = files.slice(index);
          setFiles(remaining);
          setFileTitles(fileTitles.slice(index));
          setTitle(
            remaining.length === 1
              ? fileTitles[index] || remaining[0].name
              : "",
          );
          setError(t("creativeLibrary.uploadFailed"));
          if (completed) onSaved();
          return;
        }
      }
      setFiles([]);
      if (needsCharacter) {
        try {
          const updated = await attachSubjectReferences(
            target,
            uploadedTokens,
          );
          setAttached(updated);
          setPending([]);
          onSaved();
        } catch {
          setPending(uploadedTokens);
          onSaved();
          setError(t("creativeLibrary.characterAttachFailed"));
        }
        return;
      }
      onSaved();
      if (!embedded) toast.success(t("creativeLibrary.referencesUploaded"));
      onClose();
    } finally {
      setSaving(false);
      isSubmitting.current = false;
    }
  }
  const filingFields = (
    <CreativeEditorSection
      className={needsCharacter ? "grid gap-4 sm:grid-cols-2" : undefined}
    >
      <CreativeFilingFields
        brands={brands}
        products={products}
        brandToken={brandToken}
        productToken={productToken}
        hideProduct={role === "identity"}
        disabled={
          saving ||
          pending.length > 0 ||
          Boolean(createdToken) ||
          (presetBrandToken !== undefined && presetRole !== undefined)
        }
        testIdPrefix="creative-upload"
        onChange={(brand, product) => {
          if (brand !== brandToken) {
            setSubjectToken("none");
            clearDraft();
          }
          setBrandToken(brand);
          setProductToken(product);
        }}
      />
      {needsCharacter && (
        <CharacterChoice
          brand={brand}
          value={subjectToken}
          disabled={saving || pending.length > 0}
          list={subjectList}
          creatingNew={isNewModel}
          onChange={(token, subject) => {
            setSubjectToken(token);
            setCharacterKind(subject?.kind ?? "person");
            setError("");
            clearDraft();
          }}
          onOpen={
            onOpenSubject && !files.length && !pending.length
              ? (subject) => {
                  onClose();
                  onOpenSubject(subject);
                }
              : undefined
          }
          onCreate={() => {
            setSubjectToken("none");
            setError("");
            draft.choose();
          }}
        />
      )}
      {isNewModel && (
        <div className="sm:col-span-2">
          <CreativeModelFields
            name={draft.name}
            kind={draft.kind}
            disabled={saving}
            onNameChange={draft.setName}
            onKindChange={draft.setKind}
            onSubmit={() => void save()}
          />
        </div>
      )}
      {needsCharacter && hasNoModels && !canEditBrand && (
        <Text size="sm" color="muted" className="sm:col-span-2">
          {t("creativeLibrary.noModelsNoRights")}
        </Text>
      )}
    </CreativeEditorSection>
  );
  return (
    <CreativeUploadFrame
      embedded={embedded}
      open={open}
      busy={saving}
      onClose={onClose}
      kind="creatives"
      title={t(
        presetRole === "logo"
          ? "creativeLibrary.uploadLogo"
          : "creativeLibrary.uploadReference",
      )}
      description={t(
        needsCharacter
          ? "creativeLibrary.characterUploadHint"
          : "creativeLibrary.uploadHint",
      )}
      testId="creative-upload-dialog"
      footer={
        attached ? (
          <>
            <Button variant="outline" onClick={onClose}>
              {t("creativeLibrary.done")}
            </Button>
            {onOpenSubject && (
              <Button
                variant="primary"
                onClick={() => {
                  onClose();
                  onOpenSubject(attached);
                }}
                data-testid="creative-upload-open-character"
              >
                {t("creativeLibrary.openCharacter")}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
              data-testid="creative-upload-cancel"
            >
              {t("creativeLibrary.cancel")}
            </Button>
            <Button
              variant="primary"
              className="gap-2"
              onClick={() => void save()}
              disabled={!canSave || saving}
              data-testid="creative-upload-save"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {t(
                pending.length > 0
                  ? "creativeLibrary.addToCharacter"
                  : isNewModel
                    ? "creativeLibrary.createAndUpload"
                    : presetRole === "logo"
                      ? "creativeLibrary.uploadLogo"
                      : "creativeLibrary.uploadReference",
              )}
            </Button>
          </>
        )
      }
    >
      {attached ? (
        <Text role="status" data-testid="creative-upload-attached">
          {t("creativeLibrary.addedToCharacter", { name: attached.name })}
        </Text>
      ) : (
        <>
          {!presetRole && !galleryUpload && (
            <CreativeUploadKind
              identity={role === "identity"}
              disabled={saving || pending.length > 0 || Boolean(createdToken)}
              onChange={(identity) => {
                setRole(identity ? "identity" : "product");
                if (identity) setProductToken("none");
              }}
            />
          )}
          {needsCharacter && (
            <>
              {filingFields}
              <SubjectReferenceTips
                kind={isNewModel ? draft.kind : characterKind}
              />
            </>
          )}
          <CreativeEditorSection>
            <div className="flex items-center justify-between">
              <Label>{t("creativeLibrary.referenceImage")}</Label>
              <MaterialHelpDialog
                kind={
                  role === "identity"
                    ? "identity"
                    : role === "logo"
                      ? "logo"
                      : role === "product"
                        ? "product"
                        : "style"
                }
              />
            </div>
            <FileDropzone
              accept={
                role === "logo"
                  ? ".png,.jpg,.jpeg,.webp,.svg"
                  : ".png,.jpg,.jpeg,.webp"
              }
              disabled={saving || pending.length > 0}
              multiple={presetRole !== "logo"}
              label={
                files.length > 1
                  ? t("creativeLibrary.selectedImageCount", {
                      count: files.length,
                    })
                  : (file?.name ??
                    t(
                      needsCharacter
                        ? "creativeLibrary.dropCharacterImages"
                        : "creativeLibrary.dropImage",
                    ))
              }
              hint={t(
                role === "logo"
                  ? "creativeLibrary.formatHint"
                  : "creativeLibrary.rasterFormatHint",
              )}
              onFilesSelected={(chosenFiles) => {
                const next = Array.from(chosenFiles);
                if (maxFiles !== undefined && next.length > maxFiles) {
                  setError(t("creativeLibrary.characterGalleryCapacity"));
                  return;
                }
                if (next.length) {
                  const chosen =
                    presetRole === "logo" ? next.slice(0, 1) : next;
                  setFiles(chosen);
                  setFileTitles(chosen.map((file) => file.name));
                  setTitle(chosen.length === 1 ? chosen[0].name : "");
                }
              }}
            />
          </CreativeEditorSection>
          <CreativeUploadPreviews
            files={files}
            titles={fileTitles}
            disabled={saving}
            onTitle={(index, value) =>
              setFileTitles((previous) =>
                previous.map((title, position) =>
                  position === index ? value : title,
                ),
              )
            }
          />
          {files.length > 1 && (
            <Text size="sm" color="muted">
              {t("creativeLibrary.batchUploadHelp", { count: files.length })}
            </Text>
          )}
          {files.length === 1 && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="creative-title">
                {t("creativeLibrary.title")}
              </Label>
              <Input
                id="creative-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={saving}
                data-testid="creative-upload-title"
              />
            </div>
          )}
          <CreativeReferenceOptions
            role={role}
            usage={usage}
            hideRole={embedded}
            roleDisabled={
              saving ||
              pending.length > 0 ||
              Boolean(createdToken) ||
              Boolean(presetRole)
            }
            disabled={saving}
            onRoleChange={(value) => {
              setRole(value);
              if (value === "identity") setProductToken("none");
            }}
            onUsageChange={setUsage}
          />
          {!needsCharacter && !embedded && filingFields}
          {error && (
            <Text
              size="sm"
              className="flex items-center gap-2 text-destructive"
              role="alert"
            >
              <AlertCircle className="h-4 w-4" />
              {error}
            </Text>
          )}
        </>
      )}
    </CreativeUploadFrame>
  );
}
